import * as PIXI from "pixi.js"
import { Coord } from "../../../types/Interface"
import getRotatedOffset from "../../../utils/coord/getRotatedOffset";
import getAngleBetweenPoints from "../../../utils/math/getAngleBtwPoints";
import getCenterOfTwoCoords from "../../../utils/coord/getCenterOfTwoCoords";
import getDistanceBtwPoints from "../../../utils/getDistanceBtwPoints";


const BLUE = 0x0195b8;
const DARK_BLUE = 0x015f78

export default class CompassRendererFactory {
  static drawKnob(knob: PIXI.Graphics, knobCoord: Coord, startCoord: Coord, endCoord: Coord): Coord {
    const rotRadian = getAngleBetweenPoints(startCoord, endCoord);
    knob.x = knobCoord.x;
    knob.y = knobCoord.y;

    knob
      .clear()
      .moveTo(-8, -80)
      .rect(-8, -80, 14, 40)
      .roundRect(-20, -40, 40, 76, 14)
      .fill(DARK_BLUE)

      .circle(0, 0, 8)
      .fill(0xDDDDDD)

    knob.rotation = rotRadian;
    return knobCoord
  }
  static drawNeedleLeg(needleLeg: PIXI.Graphics, startCoord: Coord, knobCoord: Coord, TOTAL_H: number) {
    const w = 18;
    const h = TOTAL_H - 22;
    const rotRadian = getAngleBetweenPoints(startCoord, knobCoord) + Math.PI / 2;

    const nLP1 = getRotatedOffset(-w / 2, -21, rotRadian);
    const nLP2 = getRotatedOffset(w / 2, -21, rotRadian);
    const nLP3 = getRotatedOffset(w / 2, -h - 23, rotRadian);
    const nLP4 = getRotatedOffset(-w / 2, -h - 23, rotRadian)

    needleLeg
      .clear()
      .moveTo(startCoord.x + nLP1.x, startCoord.y + nLP1.y)
      .lineTo(startCoord.x + nLP2.x, startCoord.y + nLP2.y)
      .lineTo(startCoord.x + nLP3.x, startCoord.y + nLP3.y)
      .lineTo(startCoord.x + nLP4.x, startCoord.y + nLP4.y)
      .lineTo(startCoord.x + nLP1.x, startCoord.y + nLP1.y)
      .stroke({ color: 0x000000, width: 2 })
      .fill(BLUE)

    const lP1 = getRotatedOffset(0, - 35, rotRadian);
    const lP2 = getRotatedOffset(0, -h - 10, rotRadian);

    needleLeg
      .moveTo(startCoord.x + lP1.x, startCoord.y + lP1.y)
      .lineTo(startCoord.x + lP2.x, startCoord.y + lP2.y)
      .stroke({ color: 0x282828, alpha: .5, width: 1 })

    const pP1 = getRotatedOffset(-4, -23, rotRadian);
    const pP2 = getRotatedOffset(4, -23, rotRadian);
    const pP3 = getRotatedOffset(0, 0, rotRadian);

    needleLeg
      .moveTo(startCoord.x + pP1.x, startCoord.y + pP1.y)
      .lineTo(startCoord.x + pP2.x, startCoord.y + pP2.y)
      .lineTo(startCoord.x + pP3.x, startCoord.y + pP3.y)
      .lineTo(startCoord.x + pP1.x, startCoord.y + pP1.y)
      .stroke({ color: 0x000000, width: 1 })
      .fill(0xe0e0e0)
  }
  static drawPencilLeg(pencilLeg: PIXI.Graphics, knobCoord: Coord, endCoord: Coord, TOTAL_H: number) {
    const w = 18;
    const h = TOTAL_H - 122;
    const thickness = 28;

    const rotRadian = getAngleBetweenPoints(knobCoord, endCoord) - Math.PI / 2;

    const nLP1 = getRotatedOffset(-w / 2, 0, rotRadian);
    const nLP2 = getRotatedOffset(w / 2, 0, rotRadian);
    const nLP3 = getRotatedOffset(w / 2, h - 20, rotRadian);
    const nLP4 = getRotatedOffset(w / 2 + 60, h - thickness + 13, rotRadian);
    const nLP5 = getRotatedOffset(w / 2 + 55, h + 13, rotRadian);
    const nLP6 = getRotatedOffset(-w / 2, h, rotRadian);

    pencilLeg
      .clear()
      .moveTo(knobCoord.x + nLP1.x, knobCoord.y + nLP1.y)
      .lineTo(knobCoord.x + nLP2.x, knobCoord.y + nLP2.y)
      .lineTo(knobCoord.x + nLP3.x, knobCoord.y + nLP3.y)
      .lineTo(knobCoord.x + nLP4.x, knobCoord.y + nLP4.y)
      .lineTo(knobCoord.x + nLP5.x, knobCoord.y + nLP5.y)
      .lineTo(knobCoord.x + nLP6.x, knobCoord.y + nLP6.y)
      .lineTo(knobCoord.x + nLP1.x, knobCoord.y + nLP1.y)
      .stroke({ color: 0x000000, width: 2 })
      .fill(BLUE)


    const lP = getRotatedOffset(0, h - 10, rotRadian);

    pencilLeg
      .moveTo(knobCoord.x, knobCoord.y)
      .lineTo(knobCoord.x + lP.x, knobCoord.y + lP.y)
      .stroke({ color: 0x282828, alpha: .5, width: 1 })

    const rP1 = getRotatedOffset(-8, 10, rotRadian);
    const rP2 = getRotatedOffset(-22, 9, rotRadian);
    const rP3 = getRotatedOffset(-14, -(thickness + 11), rotRadian);
    const rP4 = getRotatedOffset(0, -(thickness + 10), rotRadian);

    pencilLeg
      .moveTo(knobCoord.x + nLP5.x + rP1.x, knobCoord.y + nLP5.y + rP1.y)
      .lineTo(knobCoord.x + nLP5.x + rP2.x, knobCoord.y + nLP5.y + rP2.y)
      .lineTo(knobCoord.x + nLP5.x + rP3.x, knobCoord.y + nLP5.y + rP3.y)
      .lineTo(knobCoord.x + nLP5.x + rP4.x, knobCoord.y + nLP5.y + rP4.y)
      .lineTo(knobCoord.x + nLP5.x + rP1.x, knobCoord.y + nLP5.y + rP1.y)
      .fill(DARK_BLUE);
  }
  static drawPencil(pencil: PIXI.Graphics, color: number, knobCoord: Coord, endCoord: Coord) {
    const rotRadian = getAngleBetweenPoints(knobCoord, endCoord) - Math.PI / 16 * 7;

    // Tip
    const pP1 = getRotatedOffset(13, -39, rotRadian);
    const pP2 = getRotatedOffset(-13, -39, rotRadian);

    pencil
      .clear()
      .moveTo(endCoord.x, endCoord.y)
      .lineTo(endCoord.x + pP1.x, endCoord.y + pP1.y)
      .lineTo(endCoord.x + pP2.x, endCoord.y + pP2.y)
      .lineTo(endCoord.x, endCoord.y)
      .fill(0xffffff)
      .stroke({ width: 2.5, color: 0x000000 });

    //Graphite Core
    const gP1 = getRotatedOffset(3, -9, rotRadian);
    const gP2 = getRotatedOffset(-3, -9, rotRadian);

    pencil
      .moveTo(endCoord.x, endCoord.y)
      .lineTo(endCoord.x + gP1.x, endCoord.y + gP1.y)
      .lineTo(endCoord.x + gP2.x, endCoord.y + gP2.y)
      .lineTo(endCoord.x, endCoord.y)
      .fill(color)
      .stroke({ width: 2.5, color: color })

    // Barrel
    const bP3 = getRotatedOffset(-13, -280, rotRadian);
    const bP4 = getRotatedOffset(13, -280, rotRadian);

    pencil
      .moveTo(endCoord.x + pP1.x, endCoord.y + pP1.y)
      .lineTo(endCoord.x + pP2.x, endCoord.y + pP2.y)
      .lineTo(endCoord.x + bP3.x, endCoord.y + bP3.y)
      .lineTo(endCoord.x + bP4.x, endCoord.y + bP4.y)
      .lineTo(endCoord.x + pP1.x, endCoord.y + pP1.y)
      .stroke({ width: 2.5, color: 0x000000 })
      .fill(color)
  }
  static drawLock(lock: PIXI.Graphics, startCoord: Coord, knobCoord: Coord, endCoord: Coord, isActive: boolean) {
    const thickness = 13;
    const strokeColor = isActive ? 0x383838 : 0xAAAAAA;
    const fillColor = isActive ? BLUE : 0xFFFFFF;
    const shakleRad = isActive ? Math.PI / 3 : Math.PI * 5 / 6;
    const rotRadian = getAngleBetweenPoints(startCoord, endCoord);
    const centerOfNeedleLeg = getCenterOfTwoCoords(startCoord, knobCoord);
    const centerOfPencilLeg = getCenterOfTwoCoords(endCoord, knobCoord);
    const centerOfLock = getCenterOfTwoCoords(centerOfNeedleLeg, centerOfPencilLeg);
    const distanceBtwLegs = getDistanceBtwPoints(centerOfNeedleLeg, centerOfPencilLeg);
    const distanceBtwNeedleAndCenter = getDistanceBtwPoints(centerOfNeedleLeg, centerOfLock);

    Object.assign(lock, centerOfNeedleLeg);

    lock
      .clear()

      // line
      .rect(0, -thickness / 2, distanceBtwLegs, thickness)
      .stroke({ width: 3, color: strokeColor })
      .fill(fillColor)

      // circle
      .circle(distanceBtwNeedleAndCenter, 0, 18)
      .stroke({ width: 5, color: strokeColor })
      .fill(fillColor)

      // lock symbol
      .moveTo(distanceBtwNeedleAndCenter, 2)
      .arc(distanceBtwNeedleAndCenter, -7, 6, Math.PI / 2, Math.PI / 2 - shakleRad)
      .stroke({ width: 4, color: strokeColor, cap: "round" })

      .moveTo(distanceBtwNeedleAndCenter - 10, -5)
      .roundRect(distanceBtwNeedleAndCenter - 10, -5, 20, 18, 3)
      .fill(strokeColor)

      .circle(distanceBtwNeedleAndCenter, 3, 3.5)
      .fill(fillColor)

      .rotation = rotRadian
  }
}